import React, { useState } from 'react'
import { Linking, TouchableWithoutFeedback } from 'react-native'

import { TermsModal } from 'partials/TermsModal'

import { Box } from 'widgets/Box'
import { BorderButton } from 'widgets/Button/BorderButton'
import { FilledButton } from 'widgets/Button/FilledButton'
import { Separator } from 'widgets/Separator'
import { Text } from 'widgets/Text'

import { useAuth } from 'hooks/useAuth'
import { useLocalStorage } from 'hooks/useLocalStorage'
import { useQueryClient } from 'hooks/useQueryClient'
import { useRole } from 'hooks/useRole'

import { links } from 'utils/constants'
import { postUser } from 'utils/queries'

import { useCoachOnboardingModal } from 'contexts/CoachOnboardingModalContext'

import { TrainerOnboardingFlow } from './widgets/TrainerOnboardingFlow'

import { ToggleFeatureFlagsModal } from '../ToggleFeatureFlagsModal'

export const AccountView = ({ navigation, route, currentUser, isUserLoading }) => {
  const [currentFCMToken, setCurrentFCMToken] = useLocalStorage('fcmToken', null)
  const role = useRole()
  const { logout } = useAuth()
  const queryClient = useQueryClient()

  const coachOnboardingModalRef: any = useCoachOnboardingModal()

  const [isTermsOpen, setIsTermsOpen] = useState(false)
  const [isFeatureFlagsModalVisible, setIsFeatureFlagsModalVisible] = useState(false)

  const openPendingMemberships = () => {
    navigation.navigate(links.MEMBERSHIPS_PAGE, {
      tab: 'pending',
    })
  }

  const openActiveMemberships = () => {
    navigation.navigate(links.MEMBERSHIPS_PAGE, {
      tab: 'active',
    })
  }

  const enableTrainerRole = () => {
    if (!currentUser) {
      return
    }

    coachOnboardingModalRef?.current?.present()
  }

  const onGeneralLongPress = () => {
    setIsFeatureFlagsModalVisible(true)
  }

  /**
   * Unsubscribe device token from push notifications, remove all queries, and log user out.
   */
  const onLogoutPress = async () => {
    if (currentUser && currentFCMToken) {
      await postUser({
        userId: currentUser.id,
        updatedOrCreatedData: {
          pushNotificationTokens: currentUser.pushNotificationTokens?.filter(
            (pnt) => pnt !== currentFCMToken,
          ),
        },
      })

      setCurrentFCMToken(null)
    }

    await logout.mutate()
    queryClient.removeQueries()
  }

  return (
    <Box margin={{ vertical: 'xl', horizontal: 'md' }}>
      <TermsModal
        handleClose={() => setIsTermsOpen(false)}
        isOpen={isTermsOpen}
        testID='TermsModal'
      />

      {isFeatureFlagsModalVisible && (
        <ToggleFeatureFlagsModal onClose={() => setIsFeatureFlagsModalVisible(false)} />
      )}

      <Box padding={{ bottom: 'lg' }}>
        <Text bold>Member</Text>
        <Box margin={{ top: 'sm' }}>
          <FilledButton title='Active Groups' onPress={openActiveMemberships} />
        </Box>
        <Box margin={{ top: 'md' }}>
          <BorderButton title='Pending Groups' onPress={openPendingMemberships} />
        </Box>
      </Box>

      <Separator />

      <Box padding={{ vertical: 'lg' }}>
        <Text bold>Coach</Text>
        <Box margin={{ top: 'sm' }}>
          {role === 'trainer' ? (
            <TrainerOnboardingFlow navigation={navigation} route={route} />
          ) : (
            <FilledButton onPress={enableTrainerRole} title='Create a Group' />
          )}
        </Box>
      </Box>

      <Separator />

      <Box padding={{ vertical: 'lg' }}>
        {/* NOTE: Highly delayed long presses like this do not work correctly
        with the Chrome debugger open. */}
        <TouchableWithoutFeedback delayLongPress={5000} onLongPress={onGeneralLongPress}>
          <Text bold>General</Text>
        </TouchableWithoutFeedback>
        <Box margin={{ top: 'md' }}>
          <FilledButton
            title='Notification Settings'
            onPress={() => navigation.navigate(links.NOTIFICATION_SETTINGS_PAGE)}
          />
        </Box>
        <Box margin={{ top: 'md' }}>
          <BorderButton
            loading={isUserLoading}
            disabled={isUserLoading}
            title='Delete Account'
            onPress={() =>
              Linking.openURL(
                `https://share.hsforms.com/1o1RIdESPRIOaIYEhPEseDwcaljb?firstname=${currentUser?.firstName}&lastname=${currentUser?.lastName}&email=${currentUser?.email}`,
              )
            }
          />
        </Box>
        <Box margin={{ top: 'md' }}>
          <BorderButton title='Terms of Service' onPress={() => setIsTermsOpen(true)} />
        </Box>
        <Box margin={{ top: 'md' }}>
          <BorderButton title='Log Out' onPress={onLogoutPress} />
        </Box>
      </Box>
    </Box>
  )
}
